/** @module csi/dojoExtensions */
define([
	"dojo/_base/lang",
	"dojo/_base/window",
	"dojo/dnd/Moveable",
	"dojo/dom-class",
	"dojo/html",
	"dojo/store/Memory",
	"dojo/string",
	"dojo/topic",
	"dijit/Dialog",
	"dijit/DialogUnderlay",
	"dijit/focus",
	"dijit/form/Button",
	"dijit/form/ComboButton",
	"dijit/form/ValidationTextBox",
	"dijit/popup",
	"dijit/registry",
	"dijit/_WidgetBase",
	"underscore",
	"csi/html",
	"csi/locale",
	"csi/utility/stringUtility",
], function(lang, win, Moveable, domClass, dojoHtml, Memory, string, topic, Dialog, DialogUnderlay, focus, Button, ComboButton, ValidationTextBox, popup, registry, _WidgetBase, _, html, locale, {registerStringSubstituteTransform}) {
	/**
	 * CSI extensions to dojo framework.
	 */

	/**
	 * Add localization format for string substitutions in widget templates
	 * examples:
	 * ${foo:_nls}
	 * ${Class.Attr.X:_nls}
	 * @param {String} value
	 * @param {String} key
	 */
	function _nls(value, key) {
		return locale.parameterResource(key);
	}

	/**
	 * Add localization format for string substitutions in HTML fragments or for data-dojo-props that expect HTML strings (i.e. Button/MenuItem labels)
	 * Example: data-dojo-props='label: "${foo:_nlsEscaped}"'
	 * @param {String} value
	 * @param {String} key
	 */
	function _nlsEscaped(value, key) {
		return _.escape(locale.parameterResource(key));
	}

	// register functions with new string substitution api
	registerStringSubstituteTransform("_nls", _nls);
	registerStringSubstituteTransform("_nlsEscaped", _nlsEscaped);

	// add _nls function to locale so it can be used outside of templated widget
	locale._nls = _nls;
	locale._nlsEscaped = _nlsEscaped;

	// extend the widget with _nls functions
	lang.extend(_WidgetBase, {
		_nls,
		_nlsEscaped
	});

	lang.extend(dojoHtml._ContentSetter, {
		_setOrig: dojoHtml._ContentSetter.prototype.set,

		/**
		 * Override _ContentSetter to allow for variable substitution which allows for localization
		 * @param {String|DomNode|NodeList?} cont
		 * @param {Object?} params
		 */
		set: function(cont, params) {
			var w = registry.getEnclosingWidget(this.node);
			if (w && (typeof cont === "string")) {
				cont = string.substitute(cont, w, null, w);
			}

			return this._setOrig(cont, params);
		}
	});

	lang.extend(Button, {
		/**
		 * Adds materialIcon option for dojo buttons
		 * @param {string} value - name of material icon to use
		 */
		_setMaterialIconAttr: function(value) {
			this._set("materialIcon", value);
			domClass.toggle(this.iconNode, html.CSS_MATERIAL_ICON, !!value);
			this.set("iconHtml", value || "");
		},

		/**
		 * Adds custom icon content for dojo buttons
		 * @param {string} value - html content for iconNode
		 */
		_setIconHtmlAttr: function(value) {
			this._set("iconHtml", value);
			domClass.toggle(this.iconNode, "dijitNoIcon", !value && !this.iconClass);
			this.iconNode.innerHTML = value;
		}
	});

	lang.extend(ComboButton, {
		/**
		 * If buttonNode is focused, then do not switch to _popupStateNode
		 * @param {string} position
		 */
		focus: function(position) {
			const {disabled, titleNode, _popupStateNode} = this;

			if (!disabled) {
				if (!position && (focus.curNode == titleNode)) {
					position = "start";
				}

				const titleEnabled = titleNode.tabIndex >= 0;
				const popupEnabled = _popupStateNode.tabIndex >= 0;
				let node;

				if (titleEnabled === popupEnabled) {
					node = position == "start" ? titleNode : _popupStateNode;
				} else {
					node = titleEnabled ? titleNode : _popupStateNode;
				}

				focus.focus(node);
			}
		},

		/**
		 * If button contents are getting focused, then do so for button node as well
		 * @param {DOMNode} node
		 * @param {string} clazz
		 * @param {Event} evt
		 */
		_subnodeCssMouseEvent: function(node, clazz, evt) {
			ComboButton.superclass._subnodeCssMouseEvent.call(this, node, clazz, evt);

			if (node == this.titleNode) {
				ComboButton.superclass._subnodeCssMouseEvent.call(this, this.buttonNode, this.cssStateNodes.buttonNode, evt);

				// cloes dropdown if moving focus from arrow button to real button
				if (evt.type == "focus") {
					this.closeDropDown();
				}
			}
		}
	});

	lang.extend(Moveable, {
		/**
		 * @override
		 * Protect against JS error when this.node is null
		 */
		onMoveStop(mover) {
			topic.publish("/dnd/move/stop", mover);
			domClass.remove(win.body(), "dojoMove");

			if (this.node) {
				domClass.remove(this.node, "dojoMoveItem");
			}
		},
	});

	lang.extend(ValidationTextBox, {
		_computeRegexpOrig: ValidationTextBox.prototype._computeRegexp,

		/**
		 * Fix regular expression generation with ValidationTextBox when using default pattern of ".*".
		 */
		_computeRegexp: function(constraints) {
			var p = this.pattern;
			if (p === ".*") {
				this._partialre = "^(?:.*)$";
				return p;
			}

			return this._computeRegexpOrig(constraints);
		},

		_setMessageAttr: function(/*String*/ message) {
			// hide the tooltip before displaying a new one for it to update when it is already open
			if (message != this.message) {
				this.displayMessage();
			}

			this.displayMessage(message);
			this._set("message", message);
		}
	});

	lang.extend(Memory, {
		/**
		 * Adds ability to insert new object in store before an item,
		 * rather than default of inserting new object at end of store.
		 * @param object  , the item to be inserted into the store
		 * @param options , contains "before" property with data object
		 *                   before which to insert new object
		 */
		put: function(object, options) {
			// summary:
			//      Stores an object
			// object: Object
			//      The object to store.
			// options: dojo/store/api/Store.PutDirectives?
			//      Additional metadata for storing the data.  Includes an "id"
			//      property if a specific id is to be used.
			// returns: Number
			var data = this.data,
				index = this.index,
				idProperty = this.idProperty;
			var id = object[idProperty] = (options && "id" in options) ? options.id : idProperty in object ? object[idProperty] : Math.random();
			if (id in index) {
				// object exists
				if (options && options.overwrite === false) {
					throw new Error("Object already exists");
				}
				// replace the entry in data
				data[index[id]] = object;
			} else if (options?.before != null) {
				// add the new object before the given item
				var beforeRefId = options.before.id;
				if (beforeRefId in index) {
					// carve out a spot for the new item
					data.splice(index[beforeRefId], 0, object);

					// now we have to reindex
					this.setData(data);
				}
			} else {
				// append the new item
				index[id] = data.push(object) - 1;
			}
			return id;
		},
	});

	/**
	 * Add way to disable DialogUnderlay when one dialog will be closed, but another will soon open
	 */
	const _duShow = DialogUnderlay.show;
	const _duHide = DialogUnderlay.hide;
	var _duDisabled = false;

	DialogUnderlay.setDisabled = function(disable) {
		_duDisabled = disable;
	};

	DialogUnderlay.getDisabled = function() {
		return _duDisabled;
	};

	DialogUnderlay.show = function(attrs, zIndex) {
		if (!_duDisabled) {
			_duShow(attrs, zIndex);
		}
	};

	DialogUnderlay.hide = function(disable) {
		if (!_duDisabled) {
			_duHide();
		}
	};

	/**
	 * Remove ability of Toolbar to focus on first child and navigate to other children
	 * @param {Toolbar} toolbar
	 */
	function disableToolbarFocus(toolbar) {
		toolbar._getNextFocusableChild = function() { };
	}

	/**
	 * Return true if there are any popups or dialogs currently open
	 * @return {boolean}
	 */
	function hasAnyPopups() {
		return popup.getTopPopup() || (Dialog._dialogStack.length > 1);
	}

	return {
		disableToolbarFocus: disableToolbarFocus,
		hasAnyPopups: hasAnyPopups
	};
});